import { useEffect, useRef } from "react";

import { AuthShell } from "../auth/AuthShell.tsx";
import { DetailsForm } from "./DetailsForm.tsx";
import { EmailCodePanel } from "./EmailCodePanel.tsx";
import { IdentityPanel } from "./IdentityPanel.tsx";
import { SignupSteps } from "./SignupSteps.tsx";
import { SignupSummary } from "./SignupSummary.tsx";
import { useSignupController } from "./useSignupController.ts";
import type { SignupView } from "./useSignupController.ts";
import "./signup.css";

export function Signup() {
  const controller = useSignupController();
  const top = useRef<HTMLDivElement>(null);
  const stage = controller.view.kind;

  // The details form is taller than the viewport, so arriving at a new step
  // leaves the page scrolled where the last one ended — halfway down a panel
  // whose heading is off screen. Lives in the view rather than the controller
  // because it is a DOM concern, which the controller is not allowed to have.
  useEffect(() => {
    top.current?.scrollIntoView?.({ block: "start" });
  }, [stage]);

  return (
    <AuthShell>
      <div className="signup" ref={top}>
        <SignupSteps current={controller.step} complete={controller.complete} />
        <Panel controller={controller} />
      </div>
    </AuthShell>
  );
}

function Panel({ controller }: { controller: ReturnType<typeof useSignupController> }) {
  const view: SignupView = controller.view;

  switch (view.kind) {
    case "details":
      return (
        <>
          <h2 className="auth__heading">Create account</h2>
          <p className="auth__subheading">Tell us about yourself</p>
          <DetailsForm
            values={view.values}
            passwordsMatch={view.passwordsMatch}
            asksMembership={view.asksMembership}
            error={view.error}
            busy={view.busy}
            onChange={controller.updateDetail}
            onToggleMembership={controller.toggleMembership}
            onSubmit={controller.submitDetails}
          />
        </>
      );

    case "identity":
      return (
        <>
          <h2 className="auth__heading">Verify your identity</h2>
          <p className="auth__subheading">Required before accessing the registry</p>
          <IdentityPanel
            busy={view.busy}
            error={view.error}
            onBack={controller.backToDetails}
            onSkip={controller.sendCode}
          />
        </>
      );

    case "email":
      return (
        <>
          <h2 className="auth__heading">Confirm your email</h2>
          <p className="auth__subheading">
            Your account is created once this address is verified
          </p>
          <EmailCodePanel
            email={view.email}
            code={view.code}
            resendIn={view.resendIn}
            error={view.error}
            busy={view.busy}
            onChange={controller.updateCode}
            onSubmit={controller.submitCode}
            onResend={controller.resendCode}
            onBack={controller.backToDetails}
          />
        </>
      );

    case "done":
      return (
        <SignupSummary
          name={view.name}
          email={view.email}
          rows={view.rows}
          bookCall={view.bookCall}
          error={view.error}
          busy={view.busy}
          onEnter={controller.enterOndo}
        />
      );
  }
}
